// Notification System Types
// ─────────────────────────────────────────────────────────────

import { Timestamp } from 'firebase/firestore';

// ── Channels ────────────────────────────────────────────────
export type NotificationChannel = 'push' | 'email' | 'sms';

// ── Notification Categories ─────────────────────────────────
// Grouped by role for granular opt-in control
export type NotificationCategory =
  // Order lifecycle (shopper)
  | 'order_confirmation'
  | 'order_approved'
  | 'driver_assigned'
  | 'order_picked_up'
  | 'out_for_delivery'
  | 'order_delivered'
  | 'order_cancelled'
  | 'refund_processed'
  // Business alerts
  | 'new_order'
  | 'order_reminder'
  | 'low_stock'
  // Compliance — cannot be turned off
  | 'payout_hold'
  // Promotions & marketing
  | 'promotions'
  | 'new_arrivals'
  // Account & security
  | 'account_activity'
  | 'security_alerts';

/**
 * Categories a merchant cannot opt out of. These carry legally required
 * notices (e.g. that FASH is withholding their funds), so the preferences
 * UI renders them as locked rather than as a toggle.
 */
export const MANDATORY_NOTIFICATION_CATEGORIES: readonly NotificationCategory[] = [
  'payout_hold',
];

// ── User Preferences (stored on user doc) ───────────────────
export interface NotificationPreferences {
  // Master kill switch
  enabled: boolean;

  // Per-channel toggles
  channels: {
    push: boolean;
    email: boolean;
    sms: boolean;
  };

  // Per-category toggles (defaults to true if missing)
  categories: {
    [key in NotificationCategory]?: boolean;
  };

  // Quiet hours (no push between these times)
  quietHours?: {
    enabled: boolean;
    start: string; // "22:00"
    end: string;   // "07:00"
    timezone: string;
  };
}

// ── Default Preferences ─────────────────────────────────────
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  enabled: true,
  channels: {
    push: false, // requires explicit opt-in
    email: true,
    sms: false,
  },
  categories: {
    order_confirmation: true,
    order_approved: true,
    driver_assigned: true,
    order_picked_up: true,
    out_for_delivery: true,
    order_delivered: true,
    order_cancelled: true,
    refund_processed: true,
    new_order: true,
    order_reminder: true,
    low_stock: true,
    payout_hold: true, // mandatory — the toggle is display-only
    promotions: false, // opt-in only
    new_arrivals: false,
    account_activity: true,
    security_alerts: true,
  },
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '07:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
};

// ── In-App Notification Document (Firestore) ────────────────
export interface NotificationDocument {
  id: string;
  userId: string;
  title: string;
  body: string;
  category: NotificationCategory;
  
  // Optional data payload
  data?: {
    orderId?: string;
    productId?: string;
    boutiqueId?: string;
    url?: string;
    [key: string]: string | undefined;
  };

  // Status
  read: boolean;
  readAt?: Timestamp | Date | null;
  
  // Delivery tracking
  channels: NotificationChannel[];
  deliveredVia: NotificationChannel[];
  
  // Timestamps
  createdAt: Timestamp | Date;
  expiresAt?: Timestamp | Date;
}

// ── Category Display Config ─────────────────────────────────
export interface CategoryDisplayInfo {
  key: NotificationCategory;
  label: string;
  description: string;
  group: 'orders' | 'business' | 'marketing' | 'account';
  defaultEnabled: boolean;
}

export const NOTIFICATION_CATEGORIES: CategoryDisplayInfo[] = [
  // Orders
  { key: 'order_confirmation', label: 'Order Confirmations', description: 'When your order is placed and payment authorized', group: 'orders', defaultEnabled: true },
  { key: 'order_approved', label: 'Order Approved', description: 'When the boutique confirms your order', group: 'orders', defaultEnabled: true },
  { key: 'driver_assigned', label: 'Driver Assigned', description: 'When a courier is dispatched for pickup', group: 'orders', defaultEnabled: true },
  { key: 'order_picked_up', label: 'Picked Up', description: 'When the courier has your order', group: 'orders', defaultEnabled: true },
  { key: 'out_for_delivery', label: 'Out for Delivery', description: 'When your order is on the way', group: 'orders', defaultEnabled: true },
  { key: 'order_delivered', label: 'Delivered', description: 'When your order arrives', group: 'orders', defaultEnabled: true },
  { key: 'order_cancelled', label: 'Cancellations', description: 'When an order is cancelled', group: 'orders', defaultEnabled: true },
  { key: 'refund_processed', label: 'Refunds', description: 'When a refund is processed', group: 'orders', defaultEnabled: true },

  // Business
  { key: 'new_order', label: 'New Orders', description: 'Incoming orders that need your attention', group: 'business', defaultEnabled: true },
  { key: 'order_reminder', label: 'Order Reminders', description: 'Reminders for pending orders', group: 'business', defaultEnabled: true },
  { key: 'low_stock', label: 'Low Stock', description: 'When a product drops below your stock threshold', group: 'business', defaultEnabled: true },
  { key: 'payout_hold', label: 'Payout & Tax Notices', description: 'Required notices about withheld payouts and tax paperwork. Cannot be turned off.', group: 'business', defaultEnabled: true },

  // Marketing
  { key: 'promotions', label: 'Promotions & Deals', description: 'Sales, discounts, and special offers', group: 'marketing', defaultEnabled: false },
  { key: 'new_arrivals', label: 'New Arrivals', description: 'New products from boutiques you follow', group: 'marketing', defaultEnabled: false },

  // Account
  { key: 'account_activity', label: 'Account Activity', description: 'Sign-ins, profile changes, and more', group: 'account', defaultEnabled: true },
  { key: 'security_alerts', label: 'Security Alerts', description: 'Suspicious activity and security notices', group: 'account', defaultEnabled: true },
];

// ── Notification Templates ──────────────────────────────────
export interface NotificationTemplate {
  title: string;
  body: string;
  category: NotificationCategory;
}

export const ORDER_NOTIFICATION_TEMPLATES: Record<string, (data: Record<string, string>) => NotificationTemplate> = {
  ORDER_CONFIRMED: (data) => ({
    title: 'Order Confirmed!',
    body: `Your order #${data.orderNumber} has been placed. We'll notify you when the boutique approves it.`,
    category: 'order_confirmation',
  }),
  BUSINESS_APPROVED: (data) => ({
    title: 'Order Approved!',
    body: `Great news! ${data.businessName} has approved your order #${data.orderNumber}. A courier is being dispatched.`,
    category: 'order_approved',
  }),
  DRIVER_ASSIGNED: (data) => ({
    title: 'Courier on the Way!',
    body: `${data.courierName} is heading to ${data.businessName} to pick up your order.`,
    category: 'driver_assigned',
  }),
  PICKED_UP: (data) => ({
    title: 'Order Picked Up!',
    body: `Your order #${data.orderNumber} has been picked up and is heading your way.`,
    category: 'order_picked_up',
  }),
  OUT_FOR_DELIVERY: (data) => ({
    title: 'Almost There!',
    body: `Your order is out for delivery. ETA: ${data.eta || 'soon'}.`,
    category: 'out_for_delivery',
  }),
  DELIVERED: (data) => ({
    title: 'Delivered!',
    body: `Your order #${data.orderNumber} has been delivered. Enjoy your new items!`,
    category: 'order_delivered',
  }),
  CANCELLED: (data) => ({
    title: 'Order Cancelled',
    body: `Your order #${data.orderNumber} has been cancelled. ${data.reason || 'No charges were made.'}`,
    category: 'order_cancelled',
  }),
  REFUNDED: (data) => ({
    title: 'Refund Processed',
    body: `A refund of $${data.amount} for order #${data.orderNumber} has been processed. It may take 5-10 business days to appear.`,
    category: 'refund_processed',
  }),
  // Business-facing
  NEW_ORDER: (data) => ({
    title: 'New Order!',
    body: `You have a new order #${data.orderNumber} from ${data.shopperName}. Please review and approve.`,
    category: 'new_order',
  }),
  ORDER_REMINDER: (data) => ({
    title: 'Order Needs Attention',
    body: `Order #${data.orderNumber} is awaiting your approval. Auto-cancel in ${data.timeRemaining}.`,
    category: 'order_reminder',
  }),
};
